/**
 * Client → Worker API. Worker proxies PlayFab Admin API and handles Discord OAuth sessions.
 * Auth is cookie-based (HttpOnly session). Always send credentials.
 */

import type { NewsIndexEntry, NewsLanguage } from "./news-schema";
import { validateBalanceData, type BalanceData } from "./balance-schema";

export interface BalanceEntry { revision: string; updatedAt: number; data: BalanceData }
export async function getBalanceData(): Promise<BalanceEntry> {
  const entry = await req<BalanceEntry>("/api/balance-data");
  const issues = validateBalanceData(entry.data);
  if (issues.length) throw new Error(issues.join("; "));
  return entry;
}
export async function saveBalanceData(data: BalanceData): Promise<{ revision: string }> {
  const issues = validateBalanceData(data);
  if (issues.length) throw new Error(issues.join("; "));
  return req("/api/balance-data", { method: "PUT", body: JSON.stringify(data) });
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ManualMigrationPreview {
  id: string; reviewSha256: string; displayName: string | null; playtime: number;
  keyCount: number; googleLinked: boolean; googleConflict: null | {
    canResolve:boolean;displacedUid:string|null;displayName:string|null;saveVersion:number|null;playtime:number|null;
    otherProviders:string[];activeBans:number;reason:string|null;
  }; email: string | null; activeBanCount: number;
}
export function previewManualMigration(id: string) {
  return req<ManualMigrationPreview>(`/api/players/${encodeURIComponent(id)}/migrate-legacy/preview`, { method: 'POST' });
}
export type LoginMoveProvider='google'|'discord'|'apple'|'password';
export interface LoginMovePreview {
  id:string;provider:LoginMoveProvider;displayName:string|null;playtime:number;saveVersion:number;reviewSha256:string;
  conflict:NonNullable<ManualMigrationPreview['googleConflict']>;
}
export function previewLoginMove(provider:LoginMoveProvider,id:string,displacedUid:string) {
  return req<LoginMovePreview>(`/api/players/${encodeURIComponent(id)}/move-${provider}-login/preview`,{method:'POST',body:JSON.stringify({displacedUid})});
}
export function moveLogin(provider:LoginMoveProvider,id:string,displacedUid:string,reviewSha256:string) {
  return req<{moved:boolean;provider:LoginMoveProvider;operationId:string;displacedUid:string;saveVersion:number}>(`/api/players/${encodeURIComponent(id)}/move-${provider}-login`,
    {method:'POST',body:JSON.stringify({reviewSha256,displacedUid})});
}
export function migrateLegacyAccount(id: string, reviewSha256: string,resolveGoogleConflict=false) {
  return req<{migrated: boolean; saveVersion: number; emailStatus:MigrationEmailStatus;googleResolution:{operationId:string;displacedUid:string}|null; loginStatus: 'google_ready' | 'email_ready' | 'email_reset_required' | 'native_ready' | 'login_repair_required'}>(
    `/api/players/${encodeURIComponent(id)}/migrate-legacy`,
    { method: 'POST', body: JSON.stringify({reviewSha256,resolveGoogleConflict}) });
}
export type MigrationEmailStatus='pending'|'sending'|'sent'|'failed'|'superseded'|'existing_login'|'no_email';
export function getMigrationEmail(id:string) {
  return req<{status:MigrationEmailStatus|null}>(`/api/players/${encodeURIComponent(id)}/migration-email`);
}
export function retryMigrationEmail(id:string) {
  return req<{status:MigrationEmailStatus|null}>(`/api/players/${encodeURIComponent(id)}/migration-email/retry`,{method:'POST',body:'{}'});
}

export interface PurchaseAnalytics {
  todayUsd: { usdMicros: number; pending: number | null; day: string };
  since: number; until: number; timeZone: string; receiptOnly: number; pendingReports: number;
  verificationPlatforms: string; reportsEnabled: boolean;
  appleVerificationMode: string; appleRefundChecksEnabled: boolean;
  totals: { purchases: number; buyers: number; units: number | null; unknownAmounts: number | null; refundedPurchases: number | null };
  revenue: Array<{ currency: string; source: string; purchases: number; grossMicros: number; refundedMicros: number; unknownRefunds: number }>;
  products: Array<{ platform: string; productId: string; purchases: number; units: number; buyers: number; usdMicros?: number }>;
  daily: Array<{ day: string; purchases: number; usdMicros?: number; refunds?: number }>;
  /** Gross USD estimate for the selected period; pending purchases are not yet converted. */
  periodUsd?: { usdMicros: number; pending: number | null };
  /** The equal-length period immediately before the selected one. */
  previous?: { purchases: number; usdMicros: number };
  countries?: Array<{ country: string; purchases: number; usdMicros: number }>;
  buyers?: Array<{ playerId: string; displayName: string | null; purchases: number; usdMicros: number }>;
  /** WIB hours of the current day; only filled for the Today period. */
  hourly?: Array<{ hour: number; purchases: number; usdMicros: number; refunds: number }>;
  recent: Array<{ platform: string; transactionId: string; playerId: string; productId: string; purchasedAt: number;
    validatedAt: number; environment: string; quantity: number; displayName?: string | null; country?: string | null; currency: string | null; amountMicros: number | null;
    amountSource: string; verificationSource: string; refundStatus: string; refundedMicros: number | null; checkError: string | null;
    usdMicros: number | null; usdLocalRate: string | null; rawUsdLocalRate: string | null; fxRateDate: string | null;
    reportMessageId: string | null; reportError: string | null }>;
}
export function getPurchaseAnalytics(days: number, filters: { platform?: string; status?: string; q?: string } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  return req<PurchaseAnalytics>(`/api/purchases?${params}`);
}
export function sendPurchaseSample() {
  return req<{ messageId: string; url: string }>('/api/purchases/sample', {
    method: 'POST', body: JSON.stringify({ sampleId: crypto.randomUUID() }),
  });
}
export function getPurchaseDestination() {
  return req<{ destinationReady: boolean; channelId: string; guildId: string; platforms: string; reportsEnabled: boolean; appleMode: string }>('/api/purchases/destination');
}

export interface LegacyBanReview {
  playfabId: string;
  sourceTitle: string;
  source: "archive" | "playfab";
  reviewSha256: string;
  bans: Array<{ banId: string; reason: string | null; createdAt: string | number | null;
    expiresAt: string | number | null; active: boolean; corrected: boolean; sharedScope: boolean }>;
  liveReviewEnabled: boolean;
  archiveRecorded: boolean;
}
export interface LegacyBanCorrection {
  attemptId: string;
  source: LegacyBanReview["source"];
  sourceTitle: string;
  banIds: string[];
  reviewSha256: string;
  reason: string;
}
export function getLegacyBanReview(id: string, source: LegacyBanReview["source"] = "archive") {
  return req<LegacyBanReview>(`/api/legacy-bans/${encodeURIComponent(id)}?source=${source}`);
}
export function liftLegacyBans(id: string, correction: LegacyBanCorrection) {
  return req<{ confirmed: boolean; banIds: string[]; sourceTitle: string; playfabId: string; source: LegacyBanReview["source"] }>(`/api/legacy-bans/${encodeURIComponent(id)}/revoke`, {
    method: "POST", body: JSON.stringify(correction),
  });
}

export function recoveryRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return req<T>(`/api/recovery${path}`, {...init, headers: {'Content-Type': 'application/json', ...init.headers, 'X-Recovery-Review': '1'}});
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const msg =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

// ──────────────────────────────────────────────────────────────────────────
// Auth
// ──────────────────────────────────────────────────────────────────────────

export interface AdminMe {
  discordId: string;
  username: string;
  globalName: string | null;
  avatar: string | null;
  isEnvAdmin: boolean;
  role: AdminRole;
}

/** internal = full access; manager and guest are view-only for now and cannot see purchases. */
export type AdminRole = "internal" | "manager" | "guest";
export const ADMIN_ROLES: { value: AdminRole; label: string; description: string }[] = [
  { value: "internal", label: "Internal", description: "Full access, including purchases and managing admins" },
  { value: "manager", label: "Manager", description: "View only for now · no purchases" },
  { value: "guest", label: "Guest", description: "View only · no purchases" },
];

export async function fetchMe(): Promise<AdminMe> {
  return req<AdminMe>("/api/me");
}

export async function logout(): Promise<void> {
  await req("/auth/logout", { method: "POST" });
}

export function discordLoginUrl(): string {
  return "/auth/discord/login";
}

// ──────────────────────────────────────────────────────────────────────────
// PlayFab — wrapped via Worker proxy. Shapes mirror PlayFab Admin API responses
// loosely; the Worker returns the inner `data` object on success.
// ──────────────────────────────────────────────────────────────────────────

export interface PlayFabAccountInfo {
  PlayFabId: string;
  Created?: string | null;
  BackendFirstSeen?: string;
  Username?: string;
  TitleInfo?: {
    DisplayName?: string;
    Created?: string | null;
    LastLogin?: string;
  };
  PrivateInfo?: { Email?: string };
  GoogleInfo?: { GoogleEmail?: string; GoogleId?: string };
  // PlayFab returns more — narrow as you need it.
  [k: string]: unknown;
}

export type LookupBy = "playfabId" | "email" | "username" | "displayName";

export async function lookupUser(by: LookupBy, value: string): Promise<PlayFabAccountInfo> {
  const r = await req<{ UserInfo: PlayFabAccountInfo }>("/api/playfab/get-user", {
    method: "POST",
    body: JSON.stringify({ by, value }),
  });
  return r.UserInfo;
}

/**
 * Searches direct PlayFab fields and recorded legacy Google emails.
 * Returns deduplicated accounts and warnings for incomplete Google lookups.
 */
export async function searchUsers(value: string, signal?: AbortSignal): Promise<{ results: PlayFabAccountInfo[]; warnings: string[] }> {
  const r = await req<{ results: PlayFabAccountInfo[]; warnings?: string[] }>("/api/playfab/search", {
    method: "POST",
    body: JSON.stringify({ value }),
    signal,
  });
  return { results: r.results ?? [], warnings: r.warnings ?? [] };
}

export interface UserDataEntry {
  Value: string;
  LastUpdated: string;
  Permission: string;
}

export interface PlayerDataSnapshot {
  Data: Record<string, UserDataEntry>;
  source: "cloudflare" | "playfab";
  saveVersion: number | null;
}

export function getPlayerDataSnapshot(playfabId: string, keys?: string[]): Promise<PlayerDataSnapshot> {
  return req("/api/playfab/get-user-data", {
    method: "POST", body: JSON.stringify({ playfabId, keys }),
  });
}

/** Returns authoritative player data in the legacy entry shape. */
export async function getUserDataFull(
  playfabId: string,
  keys?: string[]
): Promise<Record<string, UserDataEntry>> {
  const r = await req<{ Data: Record<string, UserDataEntry> }>(
    "/api/playfab/get-user-data",
    { method: "POST", body: JSON.stringify({ playfabId, keys }) }
  );
  return r.Data ?? {};
}

/** Convenience: just the values, keyed by name. */
export async function getUserData(
  playfabId: string,
  keys?: string[]
): Promise<Record<string, string>> {
  const full = await getUserDataFull(playfabId, keys);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(full)) out[k] = v.Value;
  return out;
}

export async function updateUserData(
  playfabId: string,
  data: Record<string, string>,
  opts?: { keysToRemove?: string[]; targetLabel?: string | null; expectedSaveVersion?: number | null }
): Promise<void> {
  await req("/api/playfab/update-user-data", {
    method: "POST",
    body: JSON.stringify({
      playfabId,
      data,
      keysToRemove: opts?.keysToRemove,
      targetLabel: opts?.targetLabel,
      expectedSaveVersion: opts?.expectedSaveVersion,
    }),
  });
}

export interface InventoryItem {
  ItemId: string;
  ItemInstanceId: string;
  DisplayName?: string;
  RemainingUses?: number;
  [k: string]: unknown;
}

export async function getUserInventory(playfabId: string): Promise<InventoryItem[]> {
  const r = await req<{ Inventory: InventoryItem[] }>("/api/playfab/get-user-inventory", {
    method: "POST",
    body: JSON.stringify({ playfabId }),
  });
  return r.Inventory ?? [];
}

export interface CatalogItem {
  ItemId: string;
  ItemClass?: string;
  CatalogVersion?: string;
  DisplayName?: string;
  Description?: string;
  Tags?: string[];
  [k: string]: unknown;
}

export async function getCatalog(version?: string): Promise<CatalogItem[]> {
  const qs = version ? `?version=${encodeURIComponent(version)}` : "";
  const r = await req<{ Catalog: CatalogItem[] }>(`/api/playfab/catalog${qs}`);
  return r.Catalog ?? [];
}

export interface ItemGrant {
  itemId: string;
  annotation?: string;
}

export async function grantItems(
  playfabId: string,
  items: ItemGrant[],
  targetLabel?: string | null
): Promise<void> {
  await req("/api/playfab/grant-items", {
    method: "POST",
    body: JSON.stringify({ playfabId, items, targetLabel }),
  });
}

import type { MailReward as CloudMailReward } from "@/types/cloud-data";

/**
 * Sends in-game mail by inserting a player_mail_message row. EC2 parses
 * rewards as List<Reward>, so rewards use the game's numeric RewardType codes
 * (see AttendanceReward.cs in the game project).
 */
export type MailReward = CloudMailReward & {
  rewardType: number;
  amount: number;
};

export interface PlayerMailMessage {
  id: string;
  subject: string;
  body: string;
  rewards: MailReward[];
  createdAt: string;
  readAt: string | null;
  claimedAt: string | null;
}

export async function sendMail(
  playfabId: string,
  rewards: MailReward[],
  subject?: string,
  body?: string,
  targetLabel?: string | null
): Promise<void> {
  await req("/api/playfab/send-mail", {
    method: "POST",
    body: JSON.stringify({ playfabId, rewards, subject, body, targetLabel }),
  });
}

export async function listPlayerMail(playfabId: string): Promise<PlayerMailMessage[]> {
  const r = await req<{ mails: PlayerMailMessage[] }>(
    `/api/players/${encodeURIComponent(playfabId)}/mail`
  );
  return r.mails ?? [];
}

export async function banUser(
  playfabId: string,
  reason?: string,
  durationInHours?: number,
  targetLabel?: string | null
): Promise<void> {
  await req("/api/playfab/ban-user", {
    method: "POST",
    body: JSON.stringify({ playfabId, reason, durationInHours, targetLabel }),
  });
}

export async function revokeBans(playfabId: string, targetLabel?: string | null): Promise<void> {
  await req("/api/playfab/revoke-bans", {
    method: "POST",
    body: JSON.stringify({ playfabId, targetLabel }),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Player identity graph (session 4 — our own auth, not PlayFab)
// ──────────────────────────────────────────────────────────────────────────

export interface PlayerIdentityRow {
  provider: string;
  subject: string;
  email: string | null;
  email_verified: number;
  display_name: string | null;
  linked_at: number;
  last_login_at: number | null;
}

export async function getPlayerIdentities(playfabId: string): Promise<PlayerIdentityRow[]> {
  const r = await req<{ identities: PlayerIdentityRow[] }>(
    `/api/players/${encodeURIComponent(playfabId)}/identities`
  );
  return r.identities ?? [];
}

export async function resetPlayerPassword(playfabId: string): Promise<void> {
  await req(`/api/players/${encodeURIComponent(playfabId)}/reset-password`, { method: "POST" });
}
export const setPlayerLinkedLogin=(uid:string,email:string,password:string)=>req<{ok:boolean;email:string}>(
 `/api/players/${encodeURIComponent(uid)}/linked-login`,{method:'POST',body:JSON.stringify({email,password})});

export function deletePlayerAccount(playfabId: string, reason: string) {
  return req<{ deleted: boolean; legacyDeletionQueued: boolean }>(`/api/players/${encodeURIComponent(playfabId)}/delete-account`, {
    method: 'POST', body: JSON.stringify({ confirmAccountId: playfabId, confirmPermanentDeletion: true, reason }),
  });
}

export type UnlinkProvider = "google" | "discord" | "apple" | "password";

export async function unlinkPlayerIdentity(playfabId: string, provider: UnlinkProvider): Promise<void> {
  await req(`/api/players/${encodeURIComponent(playfabId)}/unlink`, {
    method: "POST", body: JSON.stringify({ provider }),
  });
}

export interface PlayerBanRow {
  id: number;
  playfab_id: string;
  reason: string | null;
  created_at: number;
  expires_at: number | null;
  created_by: string;
  revoked_at: number | null;
  revoked_by: string | null;
}

// Layer 2 lockout state (migrations/013_auth_failures.sql), surfaced on the
// same endpoint as bans — see server/index.ts's GET .../bans handler.
export interface AuthFailureRow {
  kind: string;
  failures: number;
  first_at: number;
  last_at: number;
  locked_until: number | null;
}

export interface PlayerBansResponse {
  bans: PlayerBanRow[];
  authFailures: AuthFailureRow[];
}

export async function getPlayerBans(playfabId: string): Promise<PlayerBansResponse> {
  const r = await req<PlayerBansResponse>(`/api/players/${encodeURIComponent(playfabId)}/bans`);
  return { bans: r.bans ?? [], authFailures: r.authFailures ?? [] };
}

// ──────────────────────────────────────────────────────────────────────────
// Account search (session 1 — find a backend account without knowing its id)
// Queries `player` / `player_identity` directly. Never PlayFab — that is
// what /api/playfab/search and searchUsers() above are for. See
// plans/current-to-do/admin-account-search/README.md.
// ──────────────────────────────────────────────────────────────────────────

export interface AccountSearchRow {
  firstSeen?: number;
  playfabId: string;
  displayName: string | null;
  providers: string[];
  /** Each linked login with its email (or display name when no email is stored). */
  logins?: { provider: string; email: string | null }[];
  email: string | null;
  lastSeen: number | null;
  banned: boolean;
  isMinted: boolean;
}

export async function getRecentAccounts(bannedOnly = false, offset = 0, playtimeCohort?: string): Promise<{ rows: AccountSearchRow[]; total: number; bannedTotal: number }> {
  return req(`/api/accounts/recent?banned=${bannedOnly}&offset=${offset}${playtimeCohort ? `&cohort=${encodeURIComponent(playtimeCohort)}` : ""}`);
}

export async function searchAccounts(
  q: string,
  limit?: number,
  bannedOnly = false,
  playtimeCohort?: string
): Promise<{ rows: AccountSearchRow[]; truncated: boolean }> {
  const params = new URLSearchParams({ q });
  if (bannedOnly) params.set("banned", "true");
  if (limit) params.set("limit", String(limit));
  if (playtimeCohort) params.set("cohort", playtimeCohort);
  const r = await req<{ rows: AccountSearchRow[]; truncated: boolean }>(
    `/api/accounts/search?${params.toString()}`
  );
  return { rows: r.rows ?? [], truncated: r.truncated ?? false };
}

// ──────────────────────────────────────────────────────────────────────────
// Title Data (Chamber of Trial)
// ──────────────────────────────────────────────────────────────────────────

/** GetTitleData's response shape is flat `Record<string, string>` — no LastUpdated wrapper. */
export async function getTitleData(keys?: string[]): Promise<Record<string, string>> {
  const qs = keys?.length ? `?keys=${encodeURIComponent(keys.join(","))}` : "";
  const r = await req<{ Data: Record<string, string> }>(`/api/playfab/title-data${qs}`);
  return r.Data ?? {};
}

/**
 * Writes one Title Data key. The server allowlists which keys this endpoint
 * accepts (only EC2_TRIAL_STAGE_3.0 today) and re-validates trial JSON
 * server-side regardless of client-side validation — the client validator is
 * UX, the server one is the guarantee.
 */
export async function setTitleData(key: string, value: string): Promise<void> {
  await req("/api/playfab/set-title-data", {
    method: "POST",
    body: JSON.stringify({ key, value }),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// News (D1) — one row per post; see migrations/003_news.sql. Replaces the
// two-key Title Data model this used to sit on. One request, one D1
// transaction — no partial-write recovery to build.
// ──────────────────────────────────────────────────────────────────────────

/** Full post shape, admin side: index-entry fields plus bodies and optional rewards. */
export interface NewsPostFull extends Omit<NewsIndexEntry, "titles"> {
  titles: Partial<Record<NewsLanguage, string>>;
  bodies: Partial<Record<NewsLanguage, string>>;
  rewards?: MailReward[];
}

/** Every post's listing fields (drafts included) — for the panel. */
export async function listNews(): Promise<NewsIndexEntry[]> {
  return req<NewsIndexEntry[]>("/api/news");
}

/** One post with bodies. */
export async function getNewsPost(id: string): Promise<NewsPostFull> {
  return req<NewsPostFull>(`/api/news/${encodeURIComponent(id)}`);
}

/** Create. The server rejects an id that already exists. */
export async function createNewsPost(post: NewsPostFull): Promise<void> {
  await req("/api/news", { method: "POST", body: JSON.stringify(post) });
}

/** Update. `id` is immutable — the server rejects an attempt to change it. */
export async function updateNewsPost(id: string, post: NewsPostFull): Promise<void> {
  await req(`/api/news/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(post) });
}

export async function deleteNewsPost(id: string): Promise<void> {
  await req(`/api/news/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Writes `sort_order` for a batch of posts in one statement. */
export async function reorderNews(order: Array<{ id: string; order: number }>): Promise<void> {
  await req("/api/news/reorder", { method: "POST", body: JSON.stringify({ order }) });
}

// ──────────────────────────────────────────────────────────────────────────
// Audit log
// ──────────────────────────────────────────────────────────────────────────

export interface AuditRow {
  id: number;
  ts: number;
  admin_discord: string;
  admin_name: string;
  action: string;
  target_playfab: string | null;
  target_label: string | null;
  summary: string;
  changed_keys: string | null;
  blob_key: string | null;
  metadata: string | null;
}

export interface AuditBlob {
  before?: Record<string, string>;
  after?: Record<string, string>;
  request?: unknown;
  response?: unknown;
}

export interface AuditListFilters {
  playfabId?: string;
  admin?: string;
  action?: string;
  fromTs?: number;
  toTs?: number;
  limit?: number;
  offset?: number;
}

export async function listAudits(
  filters: AuditListFilters = {}
): Promise<{ rows: AuditRow[]; total: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return req<{ rows: AuditRow[]; total: number }>(`/api/audit${qs ? `?${qs}` : ""}`);
}

export async function getAuditBlob(id: number): Promise<AuditBlob> {
  return req<AuditBlob>(`/api/audit/${id}/blob`);
}

// ──────────────────────────────────────────────────────────────────────────
// Admin whitelist
// ──────────────────────────────────────────────────────────────────────────

export interface AdminRow {
  discord_id: string;
  username: string | null;
  global_name: string | null;
  added_at: number;
  added_by: string | null;
  note: string | null;
  role: AdminRole;
}

export interface AdminListResponse {
  admins: AdminRow[];
  envAdmins: string[];
}

export async function listAdmins(): Promise<AdminListResponse> {
  return req<AdminListResponse>("/api/admins");
}

/** Adds an admin, or updates an existing one's note and role. Omitting `role` keeps the current one (new admins become guests). */
export async function addAdmin(discordId: string, note?: string, role?: AdminRole): Promise<void> {
  await req("/api/admins", {
    method: "POST",
    body: JSON.stringify({ discordId, note, role }),
  });
}

export async function removeAdmin(discordId: string): Promise<void> {
  await req(`/api/admins/${encodeURIComponent(discordId)}`, { method: "DELETE" });
}

// Developer accounts are exempt from save-check and player-report flags only.
export interface DevAccountRow {
  player_id: string;
  display_name: string | null;
  note: string | null;
  added_at: number;
  added_by: string | null;
}

export async function listDevAccounts(): Promise<DevAccountRow[]> {
  const response = await req<{ rows: DevAccountRow[] }>("/api/dev-accounts");
  return response.rows ?? [];
}

/** Adding an existing player updates its note. */
export async function saveDevAccount(playerId: string, note?: string): Promise<void> {
  await req("/api/dev-accounts", {
    method: "POST",
    body: JSON.stringify({ playerId, note }),
  });
}

export async function removeDevAccount(playerId: string): Promise<void> {
  await req(`/api/dev-accounts/${encodeURIComponent(playerId)}`, { method: "DELETE" });
}

export interface DevAccountCopy { playerId: string; email: string; password: string; saveVersion: number }
export function copyDevAccount(playerId: string, expectedSaveVersion: number): Promise<DevAccountCopy> {
  return req(`/api/players/${encodeURIComponent(playerId)}/copy-dev`, {
    method: "POST", body: JSON.stringify({ expectedSaveVersion }),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Leaderboard (D1) — admin view of the board this session's endpoints serve
// under /api/game/leaderboard/* (player-authenticated, not called from here).
// ──────────────────────────────────────────────────────────────────────────

export interface LeaderboardMeta {
  board: string;
  version: number;
  submissionPaused: boolean;
  entryCount: number;
}

export async function getLeaderboardMeta(board: string): Promise<LeaderboardMeta> {
  return req<LeaderboardMeta>(`/api/leaderboard/${encodeURIComponent(board)}`);
}

export async function pauseLeaderboard(board: string): Promise<void> {
  await req(`/api/leaderboard/${encodeURIComponent(board)}/pause`, { method: "POST" });
}

export async function resumeLeaderboard(board: string): Promise<void> {
  await req(`/api/leaderboard/${encodeURIComponent(board)}/resume`, { method: "POST" });
}

export async function newLeaderboardSeason(board: string): Promise<{ version: number }> {
  return req<{ version: number }>(`/api/leaderboard/${encodeURIComponent(board)}/new-season`, {
    method: "POST",
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Anti-cheat flags (D1) — one moderation queue entry per player.
// ──────────────────────────────────────────────────────────────────────────

export type FlagSource = "save_check" | "player_report" | "iap_duplicate" | "leaderboard_submit" | "full_scan";

export interface FlagRulesConfig {
  thresholds: import('./anticheat').AnticheatThresholds;
  revision: string | null;
}
export function getFlagRules(): Promise<FlagRulesConfig> { return req('/api/flags/rules'); }
export function saveFlagRules(config: FlagRulesConfig): Promise<FlagRulesConfig> {
  return req('/api/flags/rules', { method: 'PUT', body: JSON.stringify(config) });
}

export interface FullScanRun {
  id: string;
  started_at: number;
  started_by: string;
  status: "running" | "completed";
  completed_at: number | null;
  total: number;
  processed: number;
  clean: number;
  flagged: number;
  skipped: number;
  errors: number;
  issues: Array<{ player_id: string; status: string; detail: string }>;
}

export function getFullScan(): Promise<{ run: FullScanRun | null }> {
  return req("/api/flags/scan");
}
export function startFullScan(id: string, excludeIds?: string[]): Promise<{ run: FullScanRun }> {
  return req("/api/flags/scan", { method: "POST", body: JSON.stringify({ id, excludeIds }) });
}
export function getScanCheckedIds(id: string, after = ""): Promise<{ ids: string[]; next: string | null }> {
  return req(`/api/flags/scan/${encodeURIComponent(id)}/checked?after=${encodeURIComponent(after)}`);
}
export function processFullScan(id: string): Promise<{ run: FullScanRun; checkedIds?: string[] }> {
  return req(`/api/flags/scan/${encodeURIComponent(id)}/batch`, { method: "POST" });
}

export interface PlayerFlagSummary {
  player_id: string;
  first_ts: number;
  last_ts: number;
  total_count: number;
  reviewed: boolean;
  banned: boolean;
  whitelisted: boolean;
  generation: number;
  latest_source: FlagSource | null;
  latest_reasons: string | null; // JSON array of strings
}

export interface PlayerFlagRow {
  id: number;
  player_id: string;
  source: FlagSource;
  reasons: string; // JSON array of strings
  payload: string | null; // JSON, the data that triggered the flag
  reporter_id: string | null;
  ts: number;
  reviewed: number; // 0 | 1
  occurrence_count: number;
}

export interface PlayerFlagDetail {
  summary: PlayerFlagSummary;
  details: PlayerFlagRow[];
}

export interface FlagListFilters {
  source?: FlagSource;
  reviewed?: boolean;
  playerId?: string;
  includeBanned?: boolean;
  includeWhitelisted?: boolean;
  limit?: number;
  offset?: number;
}

export async function listFlags(
  f: FlagListFilters = {}
): Promise<{ rows: PlayerFlagSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (f.source) params.set("source", f.source);
  if (f.reviewed !== undefined) params.set("reviewed", String(f.reviewed));
  if (f.playerId) params.set("playerId", f.playerId);
  if (f.includeBanned) params.set("includeBanned", "true");
  if (f.includeWhitelisted) params.set("includeWhitelisted", "true");
  if (f.limit) params.set("limit", String(f.limit));
  if (f.offset) params.set("offset", String(f.offset));
  const qs = params.toString();
  return req<{ rows: PlayerFlagSummary[]; total: number }>(`/api/flags${qs ? `?${qs}` : ""}`);
}

export async function getPlayerFlags(playerId: string): Promise<PlayerFlagDetail> {
  return req(`/api/flags/player/${encodeURIComponent(playerId)}`);
}

export async function markPlayerFlagsReviewed(playerId: string, generation: number): Promise<void> {
  await req(`/api/flags/player/${encodeURIComponent(playerId)}/review`, {
    method: "POST", body: JSON.stringify({ generation }),
  });
}

export async function rescanFlags(playerId: string, generation: number): Promise<PlayerFlagDetail & {
  fallbackSources: string[]; skipped: number;
}> {
  return req(`/api/flags/player/${encodeURIComponent(playerId)}/rescan`, {
    method: "POST", body: JSON.stringify({ generation }),
  });
}

// Runtime config is a fixed D1 inventory, never an arbitrary Title Data proxy.
export async function getRuntimeConfig(key: string): Promise<string> {
  const result = await req<{ key: string; value: string }>(`/api/runtime-config/${encodeURIComponent(key)}`);
  return result.value;
}

export async function setRuntimeConfig(key: string, value: string): Promise<void> {
  await req(`/api/runtime-config/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) });
}

export interface RuntimeConfigEntry {
  key: string;
  value: string;
  updatedAt: number;
}

// Same fixed-inventory endpoint as getRuntimeConfig, but also surfaces
// updatedAt and treats a missing row (not_found) as null instead of throwing,
// so callers can render "not set" rather than an error state.
export async function getRuntimeConfigEntry(key: string): Promise<RuntimeConfigEntry | null> {
  try {
    return await req<RuntimeConfigEntry>(`/api/runtime-config/${encodeURIComponent(key)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Coupon campaigns (D1) — admin-only. Recipient PlayFab ids are only ever
// exposed through the detail endpoint below; the list endpoint carries just
// a count, and the player-facing endpoint never carries recipients at all.
// ──────────────────────────────────────────────────────────────────────────

export interface CouponCampaignRow {
  code: string;
  active: number;
  created_at: number;
  updated_at: number;
  campaign: Record<string, unknown>;
}

export interface CouponCampaignListResponse {
  campaigns: (CouponCampaignRow & { recipient_count: number })[];
}

export interface CouponCampaignDetail extends CouponCampaignRow {
  recipients: string[];
}

export interface CouponCampaignInput {
  code: string;
  campaign: Record<string, unknown>;
  recipients?: string[];
  active?: boolean;
}

export async function listCoupons(): Promise<CouponCampaignListResponse> {
  return req("/api/coupons");
}

export async function getCoupon(code: string): Promise<CouponCampaignDetail> {
  return req(`/api/coupons/${encodeURIComponent(code)}`);
}

export async function createCoupon(input: CouponCampaignInput): Promise<void> {
  await req("/api/coupons", { method: "POST", body: JSON.stringify(input) });
}

export async function updateCoupon(code: string, input: Omit<CouponCampaignInput, "code">): Promise<void> {
  await req(`/api/coupons/${encodeURIComponent(code)}`, { method: "PUT", body: JSON.stringify(input) });
}

export async function deleteCoupon(code: string): Promise<void> {
  await req(`/api/coupons/${encodeURIComponent(code)}`, { method: "DELETE" });
}

export interface PlayerRevision {
  version: number; playtime: number; ruby: number | null; gold: number | null;
  heroes: Array<{ id: number; level: number | null }> | null;
  available: boolean; source: string; createdAt: number;
}
export interface PlayerRevisions {
  currentVersion: number; rows: PlayerRevision[]; nextCursor: string | null;
}
export function getPlayerRevisions(id: string, before = "") {
  return req<PlayerRevisions>(`/api/players/${encodeURIComponent(id)}/revisions${before ? `?before=${encodeURIComponent(before)}` : ""}`);
}
export function rollbackPlayerRevision(id: string, version: number, expectedSaveVersion: number) {
  return req<{ saveVersion: number }>(`/api/players/${encodeURIComponent(id)}/restore`, {
    method: "POST", body: JSON.stringify({ version, expectedSaveVersion }),
  });
}
